import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { api, API_BASE, fmtCurrency, fmtNumber } from "@/lib/api";
import { usePermissions } from "@/context/PermissionsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DollarSign, TrendingUp, PieChart, Activity, Briefcase, Truck, HardHat,
  AlertCircle, RefreshCw, ShieldAlert, UsersRound, Wrench, Building2,
  Sparkles, ArrowRight, LayoutDashboard, Warehouse, FileDown, Mail, Send,
} from "lucide-react";
import { toast } from "sonner";
import { ProvenanceButton } from "@/components/common/ProvenanceButton";
import { DataStatusChip } from "@/components/common/DataStatusChip";
import { AIAgentPanel } from "@/components/ai/AIAgentPanel";
import { WeeklyDigestCard } from "@/components/executive/WeeklyDigestCard";
import { KpiCard as KpiCardV2, DataConfidenceBar } from "@/components/common/KpiCard";
import { ExecutiveExceptionsPanel } from "@/components/executive/ExecutiveExceptionsPanel";

// Executive Home KPIs — metric IDs match /app/backend/metric_dictionary_seed.py
const EXECUTIVE_KPI_IDS = [
  "EXE-REV-FYTD", "EXE-REV-PRIOR-FY", "EXE-REV-MTD", "EXE-GP-PCT", "EXE-EBITDA-PCT",
  "EXE-CASH", "EXE-AR-90PLUS",
  "EXE-JOBS-OPEN", "EXE-JOBS-OVERDUE", "EXE-JOBS-WIP",
  "EXE-JOBS-COMPLETED-7D", "EXE-JOBS-CNI",
  "EXE-PIPELINE", "EXE-PIPELINE-WEIGHTED",
  "EXE-ASSETS-HIGHRISK", "EXE-HSEQ-ACTIONS-OPEN", "EXE-HSEQ-INCIDENTS-30D",
  "EXE-HR-LIC-EXPIRING", "EXE-FLEET-COMPLIANCE",
  "EXE-CONTRACTS-EXPIRING", "EXE-CLIENT-CONC-TOP5", "EXE-DATA-HEALTH",
];


const FINANCIAL_KPI_IDS = new Set([
  "EXE-REV-FYTD", "EXE-REV-PRIOR-FY", "EXE-REV-MTD", "EXE-GP-PCT", "EXE-EBITDA-PCT",
  "EXE-CASH", "EXE-AR-90PLUS", "EXE-PIPELINE", "EXE-PIPELINE-WEIGHTED",
]);

// Executive KPIs grouped for management: Financial · HR & Workforce · Operational.
// `to` is the drill-down route each clickable tile navigates to; `fin` tiles are
// hidden for roles without finance access.
const EXE_SECTIONS = [
  {
    key: "financial", title: "Financial", icon: DollarSign,
    blurb: "Revenue, margin, cash & commercial risk",
    tiles: [
      { id: "EXE-REV-FYTD", to: "/finance", fin: true },
      { id: "EXE-REV-MTD", to: "/finance", fin: true },
      { id: "EXE-REV-PRIOR-FY", to: "/finance", fin: true },
      { id: "EXE-GP-PCT", to: "/finance", fin: true },
      { id: "EXE-EBITDA-PCT", to: "/finance", fin: true },
      { id: "EXE-CASH", to: "/finance", fin: true },
      { id: "EXE-AR-90PLUS", to: "/finance", fin: true },
      { id: "EXE-PIPELINE", to: "/sales", fin: true },
      { id: "EXE-PIPELINE-WEIGHTED", to: "/sales", fin: true },
      { id: "EXE-CLIENT-CONC-TOP5", to: "/crm", fin: false },
    ],
    gauges: [],
  },
  {
    key: "hr", title: "HR & Workforce", icon: UsersRound,
    blurb: "People, licences & certifications",
    tiles: [
      { id: "EXE-HR-LIC-EXPIRING", to: "/hr", fin: false },
    ],
    gauges: ["workforce"],
  },
  {
    key: "operational", title: "Operational", icon: Wrench,
    blurb: "Jobs, assets, safety, fleet & data health",
    tiles: [
      { id: "EXE-JOBS-OPEN", to: "/operations", fin: false },
      { id: "EXE-JOBS-OVERDUE", to: "/operations", fin: false },
      { id: "EXE-JOBS-WIP", to: "/operations", fin: false },
      { id: "EXE-JOBS-COMPLETED-7D", to: "/operations", fin: false },
      { id: "EXE-JOBS-CNI", to: "/operations", fin: false },
      { id: "EXE-ASSETS-HIGHRISK", to: "/assets", fin: false },
      { id: "EXE-HSEQ-ACTIONS-OPEN", to: "/hseq", fin: false },
      { id: "EXE-HSEQ-INCIDENTS-30D", to: "/hseq", fin: false },
      { id: "EXE-CONTRACTS-EXPIRING", to: "/contracts", fin: false },
      { id: "EXE-DATA-HEALTH", to: "/admin/data-governance", fin: false },
    ],
    gauges: ["fleet", "odometer"],
  },
];


function KpiCard({ enterpriseId, label, value, sub, icon: Icon, tone = "default", testid }) {
  const border = tone === "warn"  ? "border-amber-200 bg-amber-50/40"
              : tone === "good"   ? "border-emerald-200 bg-emerald-50/40"
              : tone === "danger" ? "border-rose-200 bg-rose-50/40"
              :                     "border-slate-200 bg-white";
  return (
    <div data-testid={testid} className={`rounded-lg border ${border} p-4 relative`}>
      <div className="absolute top-2 right-2"><ProvenanceButton enterpriseId={enterpriseId} size="xs" /></div>
      <div className="flex items-center gap-2 text-slate-500">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        <span className="text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="text-2xl font-display font-semibold text-slate-900 mt-1.5">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

function Gauge({ label, value, sub, enterpriseId, testid, threshold = 80, onClick }) {
  const tone = value >= threshold ? "text-emerald-700" : value >= threshold - 20 ? "text-amber-700" : "text-rose-700";
  const bar = value >= threshold ? "bg-emerald-500" : value >= threshold - 20 ? "bg-amber-500" : "bg-rose-500";
  return (
    <div data-testid={testid} onClick={onClick}
      className={`rounded-lg border border-slate-200 bg-white p-4 relative ${onClick ? "cursor-pointer hover:shadow-sm hover:border-slate-300 transition" : ""}`}>
      {enterpriseId && <div className="absolute top-2 right-2"><ProvenanceButton enterpriseId={enterpriseId} size="xs" /></div>}
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`text-3xl font-display font-semibold mt-1.5 ${tone}`}>{value}%</div>
      <div className="mt-2 h-1.5 bg-slate-200 rounded overflow-hidden">
        <div className={`h-full ${bar}`} style={{ width: `${value}%` }} />
      </div>
      {sub && <div className="text-[11px] text-slate-500 mt-1.5 truncate">{sub}</div>}
    </div>
  );
}


export default function ExecutiveHomePage() {
  const { can, loaded, user } = usePermissions();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [atRisk, setAtRisk] = useState([]);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailForm, setEmailForm] = useState({ to_email: "", recipient_name: "", note: "" });
  const [emailSending, setEmailSending] = useState(false);
  const [odo, setOdo] = useState(null);
  const [wfOpen, setWfOpen] = useState(false);
  const [wfDetail, setWfDetail] = useState(null);
  const [wfLoading, setWfLoading] = useState(false);

  const openWorkforceDetail = async () => {
    setWfOpen(true);
    if (wfDetail) return;
    setWfLoading(true);
    try {
      const r = await api.get("/executive/workforce-compliance/detail");
      setWfDetail(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load compliance detail");
    } finally { setWfLoading(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [o, a, r] = await Promise.all([
        api.get("/executive/overview"),
        api.get("/executive/alerts"),
        api.get("/executive/at-risk-clients"),
      ]);
      setOverview(o.data);
      setAlerts(a.data);
      setAtRisk(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Executive Home");
    } finally { setLoading(false); }
    api.get("/fleet/odometer-coverage").then((res) => setOdo(res.data)).catch(() => {});
  };
  useEffect(() => { if (loaded) load(); /* eslint-disable-next-line */ }, [loaded]);

  const downloadPdf = async () => {
    setPdfDownloading(true);
    try {
      const token = localStorage.getItem("gbe_token");
      const resp = await fetch(`${API_BASE}/executive/pdf/export`, {
        method: "GET",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = /filename="([^"]+)"/.exec(cd);
      const filename = m ? m[1] : `GBE_Executive_Report_${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url; link.download = filename;
      document.body.appendChild(link); link.click();
      document.body.removeChild(link); URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(`PDF export failed: ${e.message || e}`);
    } finally { setPdfDownloading(false); }
  };

  const sendEmail = async () => {
    if (!emailForm.to_email || !/^\S+@\S+\.\S+$/.test(emailForm.to_email)) {
      toast.error("Enter a valid recipient email.");
      return;
    }
    setEmailSending(true);
    try {
      const r = await api.post("/executive/pdf/email", emailForm, { timeout: 60000 });
      toast.success(`PDF emailed to ${r.data.sent_to}`);
      setEmailOpen(false);
      setEmailForm({ to_email: "", recipient_name: "", note: "" });
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === "object" && d.code === "email_not_configured") {
        toast.error(d.message);
      } else {
        toast.error(typeof d === "string" ? d : (e.message || "Email failed"));
      }
    } finally { setEmailSending(false); }
  };

  if (!loaded) return <AppLayout title="Executive · Home"><div className="h-40 shimmer" /></AppLayout>;

  const canFin = can("finance", "view");
  const kpiIds = canFin ? EXECUTIVE_KPI_IDS : EXECUTIVE_KPI_IDS.filter((id) => !FINANCIAL_KPI_IDS.has(id));
  const overdueCritical = (overview?.aged_debtors_90plus || 0) > 0;

  return (
    <AppLayout
      title="Executive · Home"
      subtitle="Ver1 hero module #4 — Board-level roll-up across CRM · Operations · Financial · HSEQ · Fleet · Workforce"
      actions={
        <div className="flex items-center gap-2">
          <AIAgentPanel agentId="executive" agentName="Executive Agent"
            buttonLabel="Ask Executive Agent" buttonVariant="filled" testid="exe-ai-agent-btn" />
          {canFin && (
            <>
              <Button size="sm" variant="outline" onClick={downloadPdf} disabled={pdfDownloading} data-testid="exe-export-pdf-btn">
                <FileDown className={`w-3.5 h-3.5 mr-1.5 ${pdfDownloading ? "animate-pulse" : ""}`} />
                Export PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEmailOpen(true)} data-testid="exe-email-pdf-btn">
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                Email PDF
              </Button>
            </>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="exe-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      }
    >
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-50 border border-slate-200 p-1 h-auto flex-wrap gap-1 justify-start">
          <TabsTrigger data-testid="exe-tab-overview" value="overview"   className="text-xs data-[state=active]:bg-white">Group Overview</TabsTrigger>
          <TabsTrigger data-testid="exe-tab-alerts"   value="alerts"     className="text-xs data-[state=active]:bg-white">Alerts & Exceptions</TabsTrigger>
          <TabsTrigger data-testid="exe-tab-at-risk"  value="at-risk"    className="text-xs data-[state=active]:bg-white">At-risk Clients</TabsTrigger>
          <TabsTrigger data-testid="exe-tab-shortcuts" value="shortcuts" className="text-xs data-[state=active]:bg-white">Module Shortcuts</TabsTrigger>
        </TabsList>

        {/* Group Overview */}
        <TabsContent value="overview" className="mt-4 space-y-4">
          {/* Data Confidence bar — Section 28.5 */}
          <DataConfidenceBar metricIds={kpiIds} />

          {/* KPIs segregated into Financial · HR & Workforce · Operational.
              Each tile is clickable and drills through to its source module.
              Financial tiles are hidden for roles without finance access. */}
          {EXE_SECTIONS.map((sec) => {
            const tiles = sec.tiles.filter((t) => canFin || !t.fin);
            const gauges = sec.gauges || [];
            if (tiles.length === 0 && gauges.length === 0) return null;
            const SecIcon = sec.icon;
            return (
              <section key={sec.key} data-testid={`exe-section-${sec.key}`} className="space-y-2.5">
                <div className="flex items-center gap-2.5">
                  <SecIcon className="w-4 h-4 text-gbe shrink-0" />
                  <h3 className="text-sm font-semibold text-slate-800">{sec.title}</h3>
                  <span className="text-[11px] text-slate-400 hidden sm:inline">{sec.blurb}</span>
                  <span className="text-[10px] text-slate-400 ml-auto">{tiles.length + gauges.length} metrics</span>
                  <div className="h-px bg-slate-200 w-8 hidden sm:block" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
                  {tiles.map((t) => (
                    <KpiCardV2 key={t.id} metricId={t.id} testId={`exe-kpi-${t.id.toLowerCase()}`}
                      onClick={() => navigate(t.to)} />
                  ))}
                  {gauges.includes("workforce") && (
                    <Gauge enterpriseId="EXE-KPI-013" label="Workforce Compliance" value={overview?.workforce_compliance_pct ?? 0}
                      sub={overview ? `${(overview.workforce_active_employees ?? 0) - (overview.workforce_noncompliant ?? 0)} of ${overview.workforce_active_employees ?? 0} staff fully current` : "Licences & certifications"}
                      onClick={openWorkforceDetail}
                      testid="exe-gauge-workforce" />
                  )}
                  {gauges.includes("fleet") && (
                    <Gauge enterpriseId="EXE-KPI-014" label="Fleet Compliance" value={overview?.fleet_compliance_pct ?? 0}
                      sub={overview ? `${(overview.fleet_active_vehicles ?? 0) - (overview.fleet_overdue ?? 0)} of ${overview.fleet_active_vehicles ?? 0} vehicles service-current` : "Service-due engine"}
                      onClick={() => navigate("/vehicles")}
                      testid="exe-gauge-fleet" />
                  )}
                  {gauges.includes("odometer") && (
                    <Gauge label="Odometer Coverage" value={odo?.coverage_pct ?? 0} threshold={70}
                      sub={odo ? `${odo.needs_follow_up} of ${odo.total_active} vehicles need a reading` : "Ampol fuel odometer readings"}
                      onClick={() => navigate("/vehicles")}
                      testid="exe-gauge-odo" />
                  )}
                </div>
              </section>
            );
          })}

          {/* Section 28.4 — Ranked cross-module exceptions with drill-down */}
          <ExecutiveExceptionsPanel />
        </TabsContent>

        {/* Alerts */}
        <TabsContent value="alerts" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">Critical alerts feed</span>
              <DataStatusChip status="ai_generated" size="xs" className="ml-1" />
              <ProvenanceButton enterpriseId="EXE-KPI-029" size="xs" className="ml-auto" />
            </div>
            {alerts && alerts.count === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No critical alerts — the group is running clean.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {(alerts?.alerts || []).map((a, i) => {
                  const dot = <span className={`w-2 h-2 rounded-full ${a.severity === "critical" ? "bg-rose-500" : a.severity === "high" ? "bg-amber-500" : "bg-slate-400"}`} />;
                  const rowInner = (
                    <>
                      {dot}
                      <span className="text-[10px] uppercase tracking-wider text-slate-500 w-24">{a.module}</span>
                      <span className="text-sm text-slate-800 flex-1">{a.message}</span>
                      <span className={`text-[10px] uppercase tracking-wider font-medium ${a.severity === "critical" ? "text-rose-700" : a.severity === "high" ? "text-amber-700" : "text-slate-600"}`}>{a.severity}</span>
                      <ProvenanceButton enterpriseId={a.enterprise_id} size="xs" />
                      {a.source_link && <ArrowRight className="w-3.5 h-3.5 text-slate-400" />}
                    </>
                  );
                  return a.source_link ? (
                    <NavLink to={a.source_link} key={i}
                             data-testid={`exe-alert-${i}`}
                             className="px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition">
                      {rowInner}
                    </NavLink>
                  ) : (
                    <li key={i} data-testid={`exe-alert-${i}`} className="px-4 py-3 flex items-center gap-3">
                      {rowInner}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* At-risk clients */}
        <TabsContent value="at-risk" className="mt-4">
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <UsersRound className="w-4 h-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-wider text-slate-600 font-medium">At-risk key clients</span>
              <DataStatusChip status="ai_generated" size="xs" className="ml-1" />
              <ProvenanceButton enterpriseId="EXE-KPI-023" size="xs" className="ml-auto" />
            </div>
            {atRisk.length === 0 ? (
              <div className="p-8 text-center text-sm text-slate-500">No key clients flagged as at-risk right now.</div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {atRisk.map((c) => (
                  <li key={c.id} data-testid={`exe-atrisk-${c.id}`} className="px-4 py-3 flex items-center gap-3">
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                    <span className="text-sm font-medium text-slate-900 flex-1">{c.name}</span>
                    <span className="text-xs text-rose-700">{c.flag}</span>
                    <NavLink to="/crm" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
                      Open CRM <ArrowRight className="w-3 h-3" />
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>

        {/* Module shortcuts */}
        <TabsContent value="shortcuts" className="mt-4 space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {[
              { to: "/crm",         label: "CRM · Clients",           icon: UsersRound, sub: `${atRisk.length} at-risk` },
              { to: "/operations",  label: "Operations",              icon: Wrench,     sub: `${overview?.open_jobs ?? 0} open jobs` },
              ...(canFin ? [{ to: "/finance", label: "Financial", icon: DollarSign, sub: fmtCurrency(overview?.revenue_12m ?? 0) }] : []),
              { to: "/assets",      label: "Asset Intelligence",      icon: Warehouse,  sub: `${overview?.high_risk_assets ?? 0} high risk` },
              { to: "/hseq",        label: "HSEQ & Sustainability",   icon: HardHat,    sub: `${overview?.open_hseq_actions ?? 0} open actions` },
              { to: "/hr",          label: "HR & Workforce",          icon: LayoutDashboard, sub: `${overview?.workforce_compliance_pct ?? 0}% compliant` },
              { to: "/vehicles",    label: "Fleet & Vehicles",        icon: Truck,      sub: `${overview?.fleet_compliance_pct ?? 0}% compliant` },
              { to: "/performance", label: "BU Performance",          icon: Building2,  sub: "Direct & Allocated" },
            ].map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`exe-shortcut-${n.to.replace('/', '')}`}
                className="rounded-lg border border-slate-200 bg-white p-4 hover:border-slate-300 hover:shadow-sm transition group">
                <n.icon className="w-5 h-5 text-slate-500 group-hover:text-slate-800" />
                <div className="font-medium text-slate-900 mt-2 flex items-center gap-1">{n.label}<ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition" /></div>
                <div className="text-xs text-slate-500 mt-0.5">{n.sub}</div>
              </NavLink>
            ))}
          </div>
          <WeeklyDigestCard />
        </TabsContent>
      </Tabs>

      <Dialog open={wfOpen} onOpenChange={setWfOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="exe-workforce-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UsersRound className="w-4 h-4" /> Workforce Compliance — non-compliant staff
            </DialogTitle>
            <DialogDescription>
              {wfDetail
                ? `${wfDetail.noncompliant} of ${wfDetail.total_active} active employees hold at least one expired, compliance-tracked licence or certification.`
                : "Active employees with an expired licence or certification."}
            </DialogDescription>
          </DialogHeader>
          {wfLoading ? (
            <div className="h-32 shimmer rounded-lg" />
          ) : !wfDetail || wfDetail.items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="exe-workforce-detail-empty">
              No non-compliant employees — every active staff member is current.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="exe-workforce-detail-list">
              {wfDetail.items.map((e) => (
                <li key={e.employee_id} data-testid={`exe-wf-emp-${e.employee_id}`} className="py-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-900 flex-1">{e.employee_name}</span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">{e.bu || e.role_title}</span>
                    <span className="text-[10px] uppercase tracking-wider font-medium text-rose-700">
                      {e.expired.length} expired
                    </span>
                  </div>
                  <div className="mt-1.5 space-y-1">
                    {e.expired.map((it, i) => (
                      <div key={`x${i}`} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full bg-rose-500" />
                        <span className="text-slate-700 flex-1 truncate">{it.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 w-20">{it.kind}</span>
                        <span className="text-rose-700 tabular-nums">expired {it.expiry_date}</span>
                      </div>
                    ))}
                    {e.expiring_soon.map((it, i) => (
                      <div key={`s${i}`} className="flex items-center gap-2 text-xs">
                        <span className="w-2 h-2 rounded-full bg-amber-500" />
                        <span className="text-slate-600 flex-1 truncate">{it.name}</span>
                        <span className="text-[10px] uppercase tracking-wider text-slate-400 w-20">{it.kind}</span>
                        <span className="text-amber-700 tabular-nums">expires {it.expiry_date}</span>
                      </div>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <NavLink to="/hr" className="text-xs text-gbe hover:underline inline-flex items-center gap-1" data-testid="exe-workforce-detail-openhr">
              Open HR &amp; Workforce <ArrowRight className="w-3 h-3" />
            </NavLink>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="exe-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-4 h-4" /> Email Executive PDF
            </DialogTitle>
            <DialogDescription>
              Send the branded Executive / BU Performance report as a PDF attachment.
              The recipient does not need a Data Hub account.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="pdf-to">Recipient email</Label>
              <Input id="pdf-to" type="email" placeholder="board@example.com"
                data-testid="exe-email-to"
                value={emailForm.to_email}
                onChange={(e) => setEmailForm({ ...emailForm, to_email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="pdf-name">Recipient name (optional)</Label>
              <Input id="pdf-name" placeholder="Board Chair"
                data-testid="exe-email-name"
                value={emailForm.recipient_name}
                onChange={(e) => setEmailForm({ ...emailForm, recipient_name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="pdf-note">Note (optional)</Label>
              <Textarea id="pdf-note" rows={3}
                placeholder="Short message that appears at the top of the email…"
                data-testid="exe-email-note"
                value={emailForm.note}
                onChange={(e) => setEmailForm({ ...emailForm, note: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailOpen(false)} disabled={emailSending}
              data-testid="exe-email-cancel">Cancel</Button>
            <Button onClick={sendEmail} disabled={emailSending} data-testid="exe-email-send">
              <Send className={`w-3.5 h-3.5 mr-1.5 ${emailSending ? "animate-pulse" : ""}`} />
              {emailSending ? "Sending…" : "Send email"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
